import type { AggregateBundle, AreaId, AssignmentPage, Filters, Meta } from './types';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

export function toQuery(filters: Filters, extra: Record<string, string | number | undefined> = {}): string {
  const params = new URLSearchParams();
  if (filters.year) params.set('year', String(filters.year));
  if (filters.month) params.set('month', String(filters.month));
  if (filters.area) params.set('area', filters.area);
  if (filters.source) params.set('source', filters.source);
  if (filters.location) params.set('location', filters.location);
  if (filters.seniority) params.set('seniority', filters.seniority);
  if (filters.workMode) params.set('workMode', filters.workMode);
  if (filters.search) params.set('q', filters.search);
  if (filters.datedOnly) params.set('dated', 'true');
  if (filters.hideRemoved) params.set('hideRemoved', 'true');
  if (filters.openOnly) params.set('openOnly', 'true');
  if (filters.showDuplicates) params.set('dupes', 'all');
  for (const tech of filters.tech) params.append('tech', tech);
  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  return params.toString();
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${BASE}${path}`, { signal, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  return (await response.json()) as T;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly payload: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Rubriken X-Requested-With kan bara sättas av vår egen kod. Ett formulär från
 * en annan sajt kan inte skicka den, vilket stänger dörren för CSRF även om
 * SameSite-kakan skulle släppa igenom något.
 */
async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'uppdragsradar' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new ApiError(response.status, String(payload.error ?? `${response.status} ${response.statusText}`), payload);
  }
  return payload as T;
}

export const fetchStats = (filters: Filters, signal?: AbortSignal): Promise<AggregateBundle> =>
  getJson(`/stats?${toQuery(filters)}`, signal);

export const fetchAssignments = (
  filters: Filters,
  page: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<AssignmentPage> => getJson(`/assignments?${toQuery(filters, { page, pageSize })}`, signal);

export const fetchMeta = (signal?: AbortSignal): Promise<Meta> => getJson('/meta', signal);

// ------------------------------------------------------------------- konto

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: 'user' | 'admin';
}

/** Vilka inloggningssätt servern har påslagna. */
export interface AuthProviders {
  sso: boolean;
  localAuth: boolean;
}

export const fetchMe = (signal?: AbortSignal): Promise<{ user: AuthUser | null } & AuthProviders> =>
  getJson('/auth/me', signal);

/** En vanlig navigering, inte fetch: Microsoft tar över fönstret och skickar tillbaka oss. */
export const ssoStartUrl = `${BASE}/auth/sso/start`;

export const login = (email: string, password: string): Promise<{ user: AuthUser }> =>
  send('POST', '/auth/login', { email, password });

export const register = (email: string, password: string, displayName: string): Promise<{ pending: boolean; message: string }> =>
  send('POST', '/auth/register', { email, password, displayName });

export const logout = (): Promise<{ ok: boolean }> => send('POST', '/auth/logout');

export const requestPasswordReset = (email: string): Promise<{ message: string }> =>
  send('POST', '/auth/request-reset', { email });

export const resetPassword = (email: string, token: string, password: string): Promise<{ user: AuthUser }> =>
  send('POST', '/auth/reset', { email, token, password });

export const changePassword = (currentPassword: string, newPassword: string): Promise<{ ok: boolean }> =>
  send('POST', '/auth/change-password', { currentPassword, newPassword });

// -------------------------------------------------------------- bevakning

export type ChannelKind = 'teams' | 'discord';

export interface WatchWebhook {
  id: string;
  kind: ChannelKind;
  label: string;
  /** Värdnamnet, aldrig hela adressen. */
  host: string;
  addedAt: string;
  lastSentAt: string | null;
}

export interface SubscriptionState {
  areas: AreaId[];
  /** Tom lista betyder alla orter. */
  locations: string[];
  /** Valda tekniker per område. Område utan poster bevakas i sin helhet. */
  technologies: Partial<Record<AreaId, string[]>>;
  /** Ta med uppdrag där källan inte anger ort respektive tekniker. */
  includeWithoutLocation: boolean;
  includeWithoutTechnologies: boolean;
  /** Falskt när mottagaren bara vill ha plingen i Teams. */
  emailEnabled: boolean;
  /** Annan mottagare än inloggningsadressen, eller null när mejlen går till den. */
  notifyEmail: string | null;
  /** Kopplade Teams-kanaler. Adresserna lämnar aldrig servern. */
  webhooks: WatchWebhook[];
  active: boolean;
  lastSentAt: string | null;
  email: string;
}

export const fetchSubscription = (signal?: AbortSignal): Promise<SubscriptionState> =>
  getJson('/subscriptions', signal);

export const saveSubscription = (
  areas: AreaId[],
  locations: string[],
  technologies: Partial<Record<AreaId, string[]>>,
  options: { includeWithoutLocation: boolean; includeWithoutTechnologies: boolean; emailEnabled: boolean; notifyEmail: string },
): Promise<SubscriptionState> => send('PUT', '/subscriptions', { areas, locations, technologies, ...options });

export const addSubscriptionWebhook = (url: string, label: string): Promise<{ webhooks: WatchWebhook[] }> =>
  send('POST', '/subscriptions/webhooks', { url, label });

export const removeSubscriptionWebhook = (id: string): Promise<{ webhooks: WatchWebhook[] }> =>
  send('DELETE', `/subscriptions/webhooks/${encodeURIComponent(id)}`);

export const testSubscriptionWebhook = (id: string): Promise<{ sent: boolean }> =>
  send('POST', `/subscriptions/webhooks/${encodeURIComponent(id)}/test`);

// -------------------------------------------------------------- medlemmar

export interface Member {
  id: string;
  email: string;
  displayName: string;
  role: 'user' | 'admin';
  status: 'pending' | 'active' | 'disabled';
  createdAt: string;
  lastLoginAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  /** Vad medlemmen bevakar, om något. */
  watch: {
    areas: AreaId[];
    locations: string[];
    technologies?: Partial<Record<AreaId, string[]>>;
    active: boolean;
    lastSentAt: string | null;
    status?: WatchStatus;
  } | null;
}

export interface WatchFunnel {
  /** Alla uppdrag efter dubbletthopslagning. */
  total: number;
  /** Nyare än bevakningens brytpunkt. */
  newSinceCutoff: number;
  /** Av dem: inom bevakade områden, efter teknikvalen. */
  inWatchedAreas: number;
  /** Av dem: på bevakad ort. Detta skulle mejlas. */
  matching: number;
}

export interface WatchStatus {
  queuePosition: number | null;
  matches: number;
  cutoff: string;
  funnel: WatchFunnel;
}

export interface QueueEntry {
  position: number;
  email: string;
  matches: number;
  waitingSince: string;
}

export interface EmailQuota {
  lastMinute: number;
  lastHour: number;
  maxPerMinute: number;
  maxPerHour: number;
  allowed: boolean;
  /** Sekunder tills nästa utskick ryms. 0 när det finns utrymme. */
  waitSeconds: number;
}

export const fetchMembers = (
  signal?: AbortSignal,
): Promise<{ members: Member[]; pendingCount: number; emailQuota?: EmailQuota; queue?: QueueEntry[] }> =>
  getJson('/members', signal);

export const setMemberStatus = (id: string, status: Member['status']): Promise<{ member: Member }> =>
  send('POST', `/members/${encodeURIComponent(id)}/status`, { status });

/** Administratörer kan bara stänga av en bevakning, aldrig slå på den. */
export const disableMemberWatch = (id: string): Promise<{ changed: boolean; member: Member }> =>
  send('POST', `/members/${encodeURIComponent(id)}/watch/disable`);

/** Skickar ett testmejl till medlemmen. Enda sättet att kontrollera utskicken
 *  utan att vänta på att ett nytt uppdrag ska dyka upp. */
export const sendMemberTestEmail = (id: string): Promise<{ sent: boolean; email: string; sentAt: string }> =>
  send('POST', `/members/${encodeURIComponent(id)}/test-email`);
